// Import Dependencies
import React, { Component, Fragment } from 'react';
import { observer, inject } from 'mobx-react';
import { Link, withRouter } from 'react-router-dom';

import Stepper from '~/common/components/Stepper';
import Button from '~/common/components/CommonButton';
import MaskedInput from 'react-text-mask';

// Import Components
import BackButton from '~/common/components/BackButton';
import Centering from '~/common/components/Centering';
import Tooltip from '~/common/components/Tooltip';
import HeaderGroup from '~/common/components/HeaderGroup';
import { clickCtaEvent, gtm, viewContentEvent } from '~/common/utils/tracking';

import {
    FLOW_STEP_NUMBERS,
    PATH_PLANS, PATH_CONFIRM_NUMBER_TRANSFER
} from '~/common/utils/consts.js';

// Import Images
import ghnumber from '~/common/images/number-port-icon.svg';

// Import Stylesheet
import styles from './transfer-current-number.css';

const PHONE_FIELD_NAME = 'ProductPhone';

@withRouter
@inject('transferNumberStore', 'formsStore', 'tollFreeNumbersStore', 'progressStore', 'trialSignupStore')
@observer
export default class TransferCurrentNumber extends Component {
    componentDidMount() {
        const { transferNumberStore, progressStore, trialSignupStore } = this.props;

        window.scroll({ top: 0, left: 0, behavior: 'smooth' });

        // checks if this is the app entry point and sets corresponding flag
        if (!progressStore.firstView) progressStore.setFirstView(FLOW_STEP_NUMBERS);

        const { isTrialFlow } = trialSignupStore;
        gtm(viewContentEvent(isTrialFlow ? 'trial flow' : 'buy flow', 'numbers', 'port number'));

        // remove flag of auto redirect once the user has seen this page once
        if (transferNumberStore.preselectedTransferNeeded) transferNumberStore.setPreselectedTransferNeeded(false);
    }

    componentWillUnmount() {
        const { formsStore } = this.props;

        // if visual error is still there (e.g. navigating through Stepper), remove it
        if (formsStore.doesFormHaveErrors([PHONE_FIELD_NAME])) formsStore.clearFieldError(PHONE_FIELD_NAME);
    }

    goToConfirmNumberTransfer(ev) {
        ev.preventDefault();
        const { transferNumberStore, formsStore, history } = this.props;

        // Make sure the number entered, minimally is 10 characters, error box if it does not have ten digits
        formsStore.validateForm([PHONE_FIELD_NAME]);
        if (formsStore.doesFormHaveErrors([PHONE_FIELD_NAME])) {
            setTimeout(() => this.transferNumberInput.inputElement.focus(), 10);
        } else {
            // establish new
            transferNumberStore.setTransferNumberValue(formsStore.ProductPhone);
            // proceed to confirm page
            history.push(PATH_CONFIRM_NUMBER_TRANSFER);
        }
    }

    setTransferNumberFormData(ev) {
        ev.preventDefault();
        // set form data, passing name of field and value
        this.props.formsStore.setFormData(PHONE_FIELD_NAME, ev.target.value);
    }

    render() {
        const { formsStore, trialSignupStore } = this.props;
        const { ProductPhone, ProductPhoneError } = formsStore;

        const showError = (ProductPhoneError.length > 0) &&
            (formsStore.parsedProductNumber.length < 10);

        return (
            <main className={styles.wrapper}>
                <Centering relative={true}>
                    <Stepper currentStep={FLOW_STEP_NUMBERS} />
                    <BackButton currentStep={FLOW_STEP_NUMBERS} />
                    <HeaderGroup>
                        <h1>Use your current number.</h1>
                        <h2>Bring your existing business number to Grasshopper, with no extra fee.</h2>
                    </HeaderGroup>
                    <img
                        alt="grasshopper number badge"
                        className={styles.icon}
                        src={ghnumber}
                    />
                    <form
                        className={styles.form}
                        onSubmit={ev => this.goToConfirmNumberTransfer(ev)}
                    >
                        <label
                            className={`${
                                styles.label} ${
                                showError ? styles['label--error'] : ''
                            }`}
                            htmlFor="transfer"
                        >Your Current Number</label>
                        <Tooltip
                            content="Please enter a 10-digit phone number"
                            direction="left"
                            hasError={showError}
                            isInlineTrigger={false}
                            isVisibleOnErrorOnly={true}
                            isVisibleOnFocusOnly={true}
                            triggerModifiers={styles['tooltip-trigger']}
                        >
                            <MaskedInput
                                autoFocus={false}
                                className={`${
                                    styles.input} ${
                                    showError ? styles['input--error'] : ''} ${
                                    ProductPhone.length > 0 ? styles['input--valid'] : ''
                                }`}
                                data-error={showError}
                                id="transfer__input"
                                mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                                name="transferNumber"
                                onChange={ev => this.setTransferNumberFormData(ev)}
                                placeholder="(___) ___-____"
                                ref={(input) => { this.transferNumberInput = input; }}
                                type="tel"
                                value={ProductPhone}
                            />
                        </Tooltip>
                        <Button
                            id="transfer__button"
                            onClick={ev => this.goToConfirmNumberTransfer(ev)}
                        >Continue</Button>
                    </form>
                    <p
                        className={styles.details}
                    >
                        {trialSignupStore.isTrialFlow ?
                            <Fragment>
                                We’ll give you a temporary number to use during your free trial.
                            </Fragment>
                            :
                            <Fragment>
                                <strong className={styles['detail-highlight']}>What happens next: </strong>After purchase, we’ll email you instructions to begin the number porting process. You will receive a temporary number for your account while the porting process completes.
                            </Fragment>
                        }

                    </p>
                    {trialSignupStore.isTrialFlow &&
                        <p className={styles.details}>
                            You will receive a form to begin the porting process once you upgrade to the paid version of Grasshopper.
                            To port your number immediately, you can
                            <Link
                                className={styles.link}
                                onClick={() => gtm(clickCtaEvent('skip your trial and buy today', PATH_PLANS, 'transfer number'))}
                                to={PATH_PLANS}
                            > skip your trial and buy today</Link>.
                        </p>
                    }
                </Centering>
            </main>
        );
    }
}
